import type { DeviceType, RoutingCondition, RoutingRule } from '@qr/shared/types';
import { Button, Callout, Field } from '@qr/ui';
import { useState } from 'react';

/**
 * Editor de reglas de destino.
 *
 * Las reglas se evaluan EN ORDEN y gana la primera que casa. Eso no es un
 * detalle de implementacion que se pueda esconder: si el cliente no entiende
 * el orden, pondra una regla general arriba y se preguntara por que las de
 * abajo no se aplican nunca. Por eso se numeran, se pueden reordenar, y el
 * destino base aparece explicitamente al final de la lista como lo que es: lo
 * que ocurre cuando ninguna regla casa.
 */

interface RoutingRulesProps {
  readonly rules: readonly RoutingRule[];
  readonly fallbackUrl: string;
  readonly disabled?: boolean;
  readonly onChange: (rules: RoutingRule[]) => void;
}

const DEVICES: { value: DeviceType; label: string }[] = [
  { value: 'mobile', label: 'Movil' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'desktop', label: 'Ordenador' },
];

const CONDITION_LABELS: Record<RoutingCondition['type'], string> = {
  device: 'Segun el dispositivo',
  country: 'Segun el pais',
  language: 'Segun el idioma',
  timeOfDay: 'Segun la hora',
  split: 'Reparto A/B',
};

function newRule(type: RoutingCondition['type']): RoutingRule {
  const id = `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

  const condition: RoutingCondition =
    type === 'device'
      ? { type: 'device', values: ['mobile'] }
      : type === 'country'
        ? { type: 'country', values: ['CO'] }
        : type === 'language'
          ? { type: 'language', values: ['es'] }
          : type === 'timeOfDay'
            ? { type: 'timeOfDay', fromHour: 9, toHour: 18 }
            : { type: 'split', percent: 50 };

  return { id, condition, url: '', enabled: true };
}

export function RoutingRules({
  rules,
  fallbackUrl,
  disabled = false,
  onChange,
}: RoutingRulesProps) {
  const [adding, setAdding] = useState<RoutingCondition['type']>('device');

  function update(index: number, rule: RoutingRule) {
    const next = [...rules];
    next[index] = rule;
    onChange(next);
  }

  function remove(index: number) {
    onChange(rules.filter((_, i) => i !== index));
  }

  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= rules.length) return;
    const next = [...rules];
    const [moved] = next.splice(index, 1);
    if (moved) next.splice(target, 0, moved);
    onChange(next);
  }

  return (
    <div className="rules">
      {rules.length === 0 ? (
        <p className="muted small">
          Sin reglas, todo el mundo va al destino principal. Añade una regla para
          enviar a sitios distintos según quien escanee.
        </p>
      ) : (
        <ol className="rules__list">
          {rules.map((rule, index) => (
            <li key={rule.id} className="rules__item">
              <div className="rules__head">
                <span className="rules__order">{index + 1}</span>
                <strong>{CONDITION_LABELS[rule.condition.type]}</strong>

                <div className="rules__controls">
                  <button
                    type="button"
                    className="rules__icon"
                    onClick={() => move(index, -1)}
                    disabled={disabled || index === 0}
                    aria-label={`Subir la regla ${index + 1}`}
                    title="Subir"
                  >
                    &#8593;
                  </button>
                  <button
                    type="button"
                    className="rules__icon"
                    onClick={() => move(index, 1)}
                    disabled={disabled || index === rules.length - 1}
                    aria-label={`Bajar la regla ${index + 1}`}
                    title="Bajar"
                  >
                    &#8595;
                  </button>
                  <label className="rules__toggle">
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      disabled={disabled}
                      onChange={(e) => update(index, { ...rule, enabled: e.target.checked })}
                    />
                    Activa
                  </label>
                  <button
                    type="button"
                    className="rules__icon rules__icon--danger"
                    onClick={() => remove(index)}
                    disabled={disabled}
                    aria-label={`Eliminar la regla ${index + 1}`}
                    title="Eliminar"
                  >
                    &#10005;
                  </button>
                </div>
              </div>

              <ConditionEditor
                condition={rule.condition}
                disabled={disabled}
                onChange={(condition) => update(index, { ...rule, condition })}
              />

              <Field
                label="Enviar a"
                type="url"
                value={rule.url}
                disabled={disabled}
                placeholder="https://tu-web.com/página-especifica"
                onChange={(e) => update(index, { ...rule, url: e.target.value })}
              />
            </li>
          ))}
        </ol>
      )}

      {/* El destino base cierra la lista: es la ultima parada cuando ninguna
          regla casa, y verlo aqui evita la confusion mas comun. */}
      <div className="rules__fallback">
        <span className="rules__order rules__order--muted">
          {rules.length + 1}
        </span>
        <div>
          <strong>El resto</strong>
          <p className="muted small">
            Si no casa ninguna regla: <code>{fallbackUrl || 'destino principal'}</code>
          </p>
        </div>
      </div>

      <div className="rules__add">
        <select
          className="qr-field__input"
          value={adding}
          disabled={disabled}
          aria-label="Tipo de regla"
          onChange={(e) => setAdding(e.target.value as RoutingCondition['type'])}
        >
          {Object.entries(CONDITION_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <Button
          variant="secondary"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...rules, newRule(adding)])}
        >
          Añadir regla
        </Button>
      </div>
    </div>
  );
}

function ConditionEditor({
  condition,
  disabled,
  onChange,
}: {
  readonly condition: RoutingCondition;
  readonly disabled: boolean;
  readonly onChange: (condition: RoutingCondition) => void;
}) {
  switch (condition.type) {
    case 'device':
      return (
        <fieldset className="rules__fieldset">
          <legend className="qr-field__label">Cuando escaneen desde</legend>
          <div className="rules__checks">
            {DEVICES.map((device) => (
              <label key={device.value}>
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={condition.values.includes(device.value)}
                  onChange={(e) => {
                    const values = e.target.checked
                      ? [...condition.values, device.value]
                      : condition.values.filter((v) => v !== device.value);
                    // Una regla sin ningun valor no casaria nunca, asi que se
                    // impide dejarla vacia.
                    if (values.length === 0) return;
                    onChange({ type: 'device', values });
                  }}
                />
                {device.label}
              </label>
            ))}
          </div>
        </fieldset>
      );

    case 'country':
      return (
        <Field
          label="Cuando escaneen desde estos países"
          value={condition.values.join(', ')}
          disabled={disabled}
          placeholder="CO, MX, ES"
          hint="Códigos de dos letras separados por comas. CO = Colombia, MX = México, ES = España."
          onChange={(e) =>
            onChange({
              type: 'country',
              values: e.target.value
                .split(',')
                .map((v) => v.trim().toUpperCase())
                .filter(Boolean),
            })
          }
        />
      );

    case 'language':
      return (
        <Field
          label="Cuando su navegador este en"
          value={condition.values.join(', ')}
          disabled={disabled}
          placeholder="es, en"
          hint="Basta la parte principal: 'es' cubre es-CO, es-MX y es-ES."
          onChange={(e) =>
            onChange({
              type: 'language',
              values: e.target.value
                .split(',')
                .map((v) => v.trim().toLowerCase())
                .filter(Boolean),
            })
          }
        />
      );

    case 'timeOfDay':
      return (
        <fieldset className="rules__fieldset">
          <legend className="qr-field__label">Cuando escaneen entre estas horas</legend>
          <div className="rules__hours">
            <input
              type="number"
              className="qr-field__input"
              min={0}
              max={23}
              value={condition.fromHour}
              disabled={disabled}
              aria-label="Hora de inicio"
              onChange={(e) =>
                onChange({ ...condition, fromHour: Number(e.target.value) })
              }
            />
            <span>y</span>
            <input
              type="number"
              className="qr-field__input"
              min={0}
              max={23}
              value={condition.toHour}
              disabled={disabled}
              aria-label="Hora de fin"
              onChange={(e) => onChange({ ...condition, toHour: Number(e.target.value) })}
            />
          </div>
          <p className="qr-field__hint">
            En horario UTC. Si la hora de fin es menor que la de inicio, el tramo
            cruza la medianoche (por ejemplo, de 22 a 6).
          </p>
        </fieldset>
      );

    case 'split':
      return (
        <div className="qr-field">
          <label className="qr-field__label" htmlFor={`split-${condition.percent}`}>
            Enviar a este destino al {condition.percent}% de la gente
          </label>
          <input
            id={`split-${condition.percent}`}
            type="range"
            min={1}
            max={99}
            value={condition.percent}
            disabled={disabled}
            onChange={(e) => onChange({ type: 'split', percent: Number(e.target.value) })}
          />
          <p className="qr-field__hint">
            El reparto es estable: quien vuelva a escanear el mismo día acaba en la
            misma versión, para que la comparacion signifique algo.
          </p>
        </div>
      );
  }
}

/** Aviso para los planes que no incluyen esta funcion. */
export function RoutingUpsell() {
  return (
    <Callout tone="info" title="Disponible en los planes de pago">
      Con las reglas de destino, un mismo código puede llevar a sitios distintos
      según el dispositivo, el idioma, el país o la hora, o repartir el trafico
      entre dos versiones para ver cual funciona mejor.
    </Callout>
  );
}
